import React from 'react';
import { Link2, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { EVENT_ICONS, formatDate } from './constants';

export default function ProvenanceChain({ history, serial }) {
  if (!history || history.length === 0) return null;

  return (
    <div className="mt-3" data-testid="provenance-chain">
      <div className="flex items-center gap-1.5 mb-3">
        <Link2 className="w-3.5 h-3.5 text-primary/60" />
        <p className="text-[10px] uppercase tracking-widest text-primary/60 font-semibold">Provenance Chain</p>
      </div>
      <div className="relative pl-5">
        {/* Vertical line */}
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gradient-to-b from-primary/30 via-white/10 to-transparent" />
        {history.map((evt, i) => {
          const evtCfg = EVENT_ICONS[evt.event] || EVENT_ICONS.registered;
          const Icon = evtCfg.icon;
          const isLast = i === history.length - 1;
          return (
            <div key={i} className="relative mb-3 last:mb-0" data-testid={`history-event-${i}`}>
              {/* Node dot */}
              <div className={`absolute -left-5 top-0.5 w-[18px] h-[18px] rounded-full ${evtCfg.bg} flex items-center justify-center border border-white/10 ${isLast ? 'ring-2 ring-primary/20' : ''}`}>
                <Icon className={`w-2.5 h-2.5 ${evtCfg.color}`} />
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 hover:border-white/10 transition-colors">
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${evtCfg.color}`}>{evt.event}</span>
                  <span className="text-[9px] text-muted-foreground/40">{formatDate(evt.timestamp)}</span>
                </div>
                <div className="text-[11px] text-muted-foreground leading-relaxed">
                  {evt.event === 'registered' && (
                    <span>Registered by <span className="text-white/80">{evt.by_name || 'unknown'}</span></span>
                  )}
                  {(evt.event === 'sold' || evt.event === 'gifted' || evt.event === 'lent') && (
                    <span className="flex items-center gap-1 flex-wrap">
                      <span className="text-white/80">{evt.by_name || '?'}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
                      <span className="text-white/80">{evt.to_name || '?'}</span>
                    </span>
                  )}
                  {evt.event === 'returned' && (
                    <span>Returned by <span className="text-white/80">{evt.by_name || '?'}</span> to <span className="text-white/80">{evt.to_name || '?'}</span></span>
                  )}
                  {evt.event === 'lost' && (
                    <span>Marked lost by <span className="text-white/80">{evt.by_name || '?'}</span></span>
                  )}
                  {evt.event === 'recycled' && (
                    <span>Retired/recycled by <span className="text-white/80">{evt.by_name || '?'}</span></span>
                  )}
                  {evt.event === 'photo_verified' && (
                    <span>Condition photo verified by <span className="text-white/80">{evt.by_name || '?'}</span></span>
                  )}
                  {evt.event === 'found' && (
                    <span>Found by <span className="text-white/80">{evt.by_name || '?'}</span></span>
                  )}
                  {evt.event === 'deleted' && (
                    <span>Deleted by <span className="text-white/80">{evt.by_name || '?'}</span></span>
                  )}
                </div>
                {evt.notes && <p className="text-[10px] text-muted-foreground/50 italic mt-1">"{evt.notes}"</p>}
              </div>
            </div>
          );
        })}
      </div>
      {/* Hash footer */}
      <div className="mt-2 flex items-center gap-1.5 px-1">
        <div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
        <p className="text-[8px] font-mono text-muted-foreground/30 tracking-wider">{serial} / {history.length} event{history.length !== 1 ? 's' : ''} on chain</p>
      </div>
    </div>
  );
}
